import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, User, Upload, Radio, Film, ChevronRight, Music2, Zap } from "lucide-react";

const NAV = [
  { label: "Overview", icon: LayoutDashboard, href: "/dashboard" },
  { label: "My Profile", icon: User, href: "/dashboard/profile" },
  { label: "Upload Content", icon: Upload, href: "/dashboard/upload" },
  { label: "My Content", icon: Film, href: "/dashboard/content" },
  { label: "Radio Shows", icon: Radio, href: "/dashboard/shows" },
  { label: "My Station", icon: Radio, href: "/dashboard/station" },
  { label: "Auto-DJ", icon: Music2, href: "/dashboard/autodj" },
  { label: "Go Live", icon: Zap, href: "/dashboard/live" },
];

export function DashboardHome() {
  const { profile, role } = useAuth();
  const cards = [
    { label: "Upload Content", desc: "Upload videos, audio, DJ sets & more", href: "/dashboard/upload", color: "green" },
    { label: "Manage Shows", desc: "Create and schedule radio shows", href: "/dashboard/shows", color: "purple" },
    { label: "My Station", desc: "Configure your radio station", href: "/dashboard/station", color: "purple" },
    { label: "Auto-DJ", desc: "Manage automated filler tracks for your station", href: "/dashboard/autodj", color: "green" },
    { label: "Edit Profile", desc: "Update bio, links, and branding", href: "/dashboard/profile", color: "green" },
  ];

  return (
    <div className="space-y-6">
      {role !== "creator" && role !== "admin" && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4">
          <p className="text-yellow-400 text-sm font-medium">Your creator account is pending approval.</p>
          <p className="text-yellow-400/70 text-xs mt-1">An admin will review your application shortly.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map((card) => (
          <Link
            key={card.href}
            to={card.href}
            data-testid={`dashboard-card-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`bg-[#111] border border-white/5 rounded-2xl p-5 hover:bg-[#1a1a1a] transition-all ${
              card.color === "green" ? "hover:border-[#00ff77]/30" : "hover:border-[#B026FF]/30"
            }`}
          >
            <h3 className={`font-bold text-base mb-1 ${card.color === "green" ? "text-[#00ff77]" : "text-[#B026FF]"}`}>
              {card.label}
            </h3>
            <p className="text-zinc-500 text-sm">{card.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { profile } = useAuth();
  const { pathname } = useLocation();

  return (
    <div data-testid="dashboard" className="min-h-[80vh]">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-white">Creator Dashboard</h1>
        <p className="text-zinc-500 text-sm mt-1">Welcome back, {profile?.displayName || "Creator"}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sub-nav — visible on all dashboard sub-pages */}
        <div className="space-y-1">
          {NAV.map(({ label, icon: Icon, href }) => {
            const active = href === "/dashboard" ? pathname === "/dashboard" : pathname.startsWith(href);
            return (
              <Link
                key={href}
                to={href}
                data-testid={`dash-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  active ? "bg-[#00ff77]/10 text-[#00ff77]" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={16} />
                <span className="text-sm font-medium">{label}</span>
                {active && <ChevronRight size={14} className="ml-auto" />}
              </Link>
            );
          })}
        </div>

        {/* Sub-page content rendered via nested routing */}
        <div className="lg:col-span-3">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
